import React, {useEffect, useState, useCallback} from "react";
import translate from "components/translations/translations.wrapper";
import {autoMenuStyle} from "utils/reactSelectTheme";
import UserSearch from "components/input/UserSearch.component";
import SettingsPreferencesCard from "components/newSettings/Settings.preferences.card";
import {Controller, useForm} from "react-hook-form";
import {useSelector} from "react-redux";
import {currentPreferenceSelector} from "components/settings/preferences.selector";
import {SubstituteActivePeriod} from "types/nextTask";
import DateFilter from "components/input/CalendarInput.component";
import moment from "moment";
import * as reduxSelectors from "components/store/application.reducers.js";
import {Form} from "react-bootstrap";
import debounce from "lodash/debounce";
import * as Api from "utils/api/api";
import ErrorInContext from "components/common/errorInContext";
import _ from "lodash";
import DisplayProgress from "components/common/displayProgress";
import {DropdownSelect} from "components/common/dropdown/DropdownSelect.component";
import {DropdownSelectTypes, DropdownPosition} from "types/gaiaComponents/dropdownButton.types";
import { Loader, LoaderSize, LoaderType } from "components/common/Loader.component";


export const SubstituteContent = (props: any): React.ReactElement => {
    const settings = useSelector(currentPreferenceSelector);
    const userConfiguration = useSelector(reduxSelectors.getUsersData);
    const [context, setContext] = React.useState(useSelector(reduxSelectors.getUsersCurrentContextId));
    const userLanguage = useSelector(reduxSelectors.getLanguage);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<{ title: string, text: string, more?: string | null } | null>(null);
    const [companies, setCompanies] = useState<any[]>([]);

    const initializeSubstituteSettings = (substitute: any) => {
        const object = {
            ...substitute,
            from: moment(substitute.from || new Date()),
            to: moment(substitute.to || new Date()),
        }
        if (!substitute.active) {
            object.activeOption = SubstituteActivePeriod.INACTIVE;
            object.from = moment();
            object.to = moment();
        } else {
            object.activeOption = substitute.to ? SubstituteActivePeriod.ACTIVE_PERIOD : SubstituteActivePeriod.ACTIVE_NOW;
        }
        return object;
    }

    const [newSubstituteSettings, setNewSubstituteSettings] = useState(null);

    const {control} = useForm();


    const submitChanges = async (updatedSettings: any, context: number) => {
        if (loading) {
            return;
        }
        setError(null);
        setLoading(true);

        const newSubstitute = _.cloneDeep(settings.substitute);
        delete newSubstitute.substituteMessage;
        delete newSubstitute.activeOption;
        delete newSubstitute.to;

        const dataToSend = {
            ...settings,
            substitute: {
                ...newSubstitute,
                substituteId: updatedSettings.substituteId,
                version: updatedSettings.version,
                substituteName: updatedSettings.substituteName,
                active: updatedSettings.activeOption !== SubstituteActivePeriod.INACTIVE,
                from: updatedSettings.activeOption === SubstituteActivePeriod.ACTIVE_PERIOD ? moment(updatedSettings.from).hours(12).format("YYYY-MM-DD") : moment().hours(12).format("YYYY-MM-DD")
            }
        };
        if (updatedSettings.activeOption === SubstituteActivePeriod.ACTIVE_PERIOD) {
            dataToSend.substitute.to = moment(updatedSettings.to).hours(12).format("YYYY-MM-DD");
        }


        try {
            await Api.updateUserSettings(dataToSend, context);
            getSubstituteForContext(context).then(() => {
                setLoading(false);
            });


        } catch (error) {
            setError({
                title: props.translate("settings.preferences.saveErrorTitle"),
                text: props.translate("settings.preferences.saveErrorText"),
                more: error.errorMessages || null
            });
            setLoading(false);
        }
    };

    const debouncedSubmit = useCallback(
        debounce(async (data: any, context) => {
            try {
                await submitChanges(data, context);
            } catch (error) {
                console.error("Error in submitChanges:", error);
            }
        }, 500),
        [context]
    );

    useEffect(() => {
        getCompaniesForSubstitutes().then();
        getSubstituteForContext(context).then();
    }, []);

    useEffect(() => {
        return () => {
            debouncedSubmit.cancel();
        };
    }, [debouncedSubmit]);

    const getCompaniesForSubstitutes = async () => {
        try {
            const companies = await Api.getCompaniesForSubstitutes(context);
            setCompanies(companies);
        } catch (error) {
            setError({
                title: props.translate("settings.substitute.companiesErrorTitle"),
                text: props.translate("settings.substitute.companiesErrorText"),
                more: error.errorMessages || null
            });
        }
    }

    const getSubstituteForContext = async (companyId: number | string) => {
        try {
            const settings = await Api.getUserSettings(companyId);
            const updatedSettings = initializeSubstituteSettings(settings.substitute);
            setNewSubstituteSettings(updatedSettings);
        } catch (error) {
            setError({
                title: error.errorTitle || "",
                text: error.errorMessages || null,
            });
        }
    }

    const changeUser = (value: any) => {
        if (value?.substituteId === newSubstituteSettings.substituteId) return;

        const updatedSettings = {
            ...newSubstituteSettings,
            active: true,
            activeOption: SubstituteActivePeriod.ACTIVE_NOW,
            from: moment(),
            substituteName: value?.label || null,
            substituteId: value?.value || null,
        };

        setNewSubstituteSettings(updatedSettings);
        debouncedSubmit(updatedSettings, context);
    };

    const changeActiveOption = (value: SubstituteActivePeriod) => {
        const updatedSettings = {
            ...newSubstituteSettings,
            activeOption: value,
            active: value !== SubstituteActivePeriod.INACTIVE,
            from: value === SubstituteActivePeriod.ACTIVE_PERIOD ? moment() : newSubstituteSettings.from,
            to: value === SubstituteActivePeriod.ACTIVE_PERIOD ? newSubstituteSettings.to : moment(),
        };

        setNewSubstituteSettings(updatedSettings);
        debouncedSubmit(updatedSettings, context);
    };

    const changeDates = (date: Date, type: "from" | "to") => {
        const updatedSettings = {...newSubstituteSettings, [type]: moment(date)};

        if (type === "from" && date > newSubstituteSettings.to) {
            updatedSettings.to = moment(date);
        }
        setNewSubstituteSettings(updatedSettings);
        debouncedSubmit(updatedSettings, context);
    };


    const lang = moment().locale(userLanguage === "en" ? "en-gb" : userLanguage);
    const valueFormatter = lang.localeData()._longDateFormat.L;

    const renderDateController = (name: string, minDate: any, selectedValue: Date, onChange: (value: Date) => void) => {
        const adjustedValue = moment(selectedValue).hours(12);

        return (
            <Controller
                name={name}
                control={control}
                render={({field: {onBlur}}) => (
                    <DateFilter
                        style="popup"
                        isDisabled={newSubstituteSettings.activeOption !== SubstituteActivePeriod.ACTIVE_PERIOD || loading}
                        translate={props.translate}
                        onBlur={onBlur}
                        format={valueFormatter}
                        userLanguage={userLanguage}
                        minDate={minDate}
                        selectedValue={adjustedValue}
                        onChange={onChange}
                        testid={`__tst_${name}`}
                    />
                )}
            />
        );
    };


    return (
        <form>
            {error && <ErrorInContext {...error}
                                      onCloseCallback={() => {
                                          setError(null);
                                      }}
            />}


            {newSubstituteSettings ?
                <SettingsPreferencesCard
                    title={props.translate("settings.substitute.substituteTitle")}
                    contentInput={
                        <div className={"d-flex w-100"}>
                            <div className="col-md-7 mx-0 px-0 ast_user-search">
                                <div className={"mb-8"}>{props.translate("settings.substitute.selectSubstitute")}</div>
                                <UserSearch
                                    className="col-md-12 mx-0 px-0"
                                    propagateValue={changeUser}
                                    companyId={context}
                                    value={{
                                        label: newSubstituteSettings.substituteName,
                                        value: newSubstituteSettings.substituteId,
                                    }}
                                    disabled={loading}
                                    placeholder={props.translate("settings.substitute.selectUser")}
                                    clearable
                                    styles={autoMenuStyle}
                                    ariaLabel={
                                        newSubstituteSettings.substituteName ||
                                        props.translate("settings.substitute.selectUser")
                                    }
                                    translate={props.translate}
                                />

                                {Object.values(SubstituteActivePeriod).map((option) => (
                                    <div className={"d-flex flex-column w-100"} key={option}>
                                        <Form.Check
                                            name="activeOption"
                                            value={option}
                                            id={option}
                                            type="radio"
                                            className="radio"
                                            disabled={!newSubstituteSettings.substituteId || loading}
                                            checked={newSubstituteSettings.activeOption === option}
                                            onChange={() => changeActiveOption(option)}
                                            aria-label={
                                                props.translate("settings.substitute.afterHandle ") +
                                                props.translate(`settings.substitute.${option}`)
                                            }
                                            label={
                                                <span className={"font-16"}>
                                            {props.translate(`settings.substitute.${option}`)}
                                        </span>
                                            }
                                            data-testid={`__tst_${option}`}
                                        />
                                    </div>
                                ))}

                                <div className="d-flex flex-row justify-content-between align-items-center mt-2 ">
                                    <span className={"me-2"}>{props.translate("settings.substitute.from")}</span>
                                    {renderDateController("from", moment(), newSubstituteSettings.from?.toDate(), (value) => changeDates(value, "from"))}
                                    <span className={"ms-4 me-2"}>{props.translate("settings.substitute.to")}</span>
                                    {renderDateController("to", newSubstituteSettings.from?.toDate(), newSubstituteSettings.to?.toDate(), (value) => changeDates(value, "to"))}
                                </div>
                            </div>

                            <div className="col-md-5 ms-4 d-flex flex-column mb-4">
                                {companies?.length > 0 &&
                                    <div className={"w-100 mb-4"}>
                                        <div className={"mb-8"}>{props.translate("settings.substitute.forCompany")}</div>
                                        <DropdownSelect
                                            id="substitute-companies"
                                            type={DropdownSelectTypes.VALUE}
                                            disabled={loading}
                                            value={companies.find((company) => company.id === context)?.name || userConfiguration?.companyName || ""}
                                            position={DropdownPosition.BOTTOM_RIGHT}
                                            items={companies.map((company) => ({
                                                key: company.id.toString(),
                                                text: company.name,
                                                onClick: () => {
                                                    setContext(Number(company.id));
                                                    getSubstituteForContext(company.id).then();
                                                },
                                                selected: company.id === context
                                            }))}
                                        />
                                    </div>
                                }


                                {newSubstituteSettings.substituteMessage &&
                                    <ErrorInContext title={newSubstituteSettings.substituteMessage.messageTitle}
                                                    text={newSubstituteSettings.substituteMessage.message}
                                    />
                                }
                            </div>
                        </div>
                    }

                />
                :
                <DisplayProgress message={props.translate("userSearch.loading")}/>
            }
            {loading &&
                <div className="ms-4">
                    <Loader type={LoaderType.Default} size={LoaderSize.Medium} text={props.translate("settings.substitute.savingChanges")}/>
                </div>}
        </form>
    );
};

const withTranslations = translate(SubstituteContent);
export default withTranslations;
